"use client";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { CalendarIcon } from "lucide-react";
import { toast } from "sonner";
import { useCardModal } from "@/hooks/use-card-model";
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CardWithAllRelationalInfo } from "@/types/funnelbuilder-types/funnelbuilder-types";
import { format } from "date-fns";
import { Doc, Id } from "@/convex/_generated/dataModel";
import { cn, Avatar as NextUIAvatar, User } from "@heroui/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { SelectContent } from "@/components/ui/select";
import { Select, SelectTrigger, SelectItem, SelectValue } from "@/components/ui/select";
import { api } from "@/convex/_generated/api";
import { useMutation, useQuery } from "convex/react";
import moment from "moment";
import TagCreator from "@/components/funnelbuilder/tag-creator";
import { useAuth } from "@clerk/nextjs";
interface ExtraInfoProps {
  data: CardWithAllRelationalInfo;
}
export const ExtraInfo = ({
  data
}: ExtraInfoProps) => {
  const {
    orgId
  } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [tags, setTags] = useState<Doc<"labels">[]>([]);
  const [allTeamMembers, setAllTeamMembers] = useState<Doc<"saasUsers">[]>([]);
  const [assignedTo, setAssignedTo] = useState(data.assignedUserId || "");
  const [date, setDate] = useState<Date>(data.dueDate ? new Date(data.dueDate) : new Date());

  // Define mutations for updating card fields
  const updateCardAllInfoMutation = useMutation(api.pmAi.todos.updateCardAllInfo);
  const updateSubTodoAllInfoMutation = useMutation(api.pmAi.subTodos.updateSubTodoAllInfo);
  const orgTeamMembers = useQuery(api.memberships.allOrgMembers);
  const labelsResponse = useQuery(api.pmAi.labels.getLabelByLabelId, {
    labelIds: data.labelIds
  });
  const availableLabelsResponse = useQuery(api.pmAi.labels.getLabels);
  const [availableLabels, setAvailableLabels] = useState<Doc<"labels">[]>([]);
  useEffect(() => {
    if (labelsResponse?.data) {
      setTags(labelsResponse.data as Doc<"labels">[]);
    }
    if (availableLabelsResponse?.data) {
      setAvailableLabels(availableLabelsResponse.data);
    }
    if (orgTeamMembers?.error) {
      toast.error(orgTeamMembers.error.message);
    }
    if (data && orgTeamMembers?.data) {
      const filteredMembers = orgTeamMembers.data.filter((member: Doc<"saasUsers"> | null): member is NonNullable<Doc<"saasUsers">> => member !== null);
      setAllTeamMembers(filteredMembers);
    }
  }, [data, orgTeamMembers, labelsResponse?.data, availableLabelsResponse?.data]);
  const handleDateChange = async (newDate: Date | undefined) => {
    if (!newDate) return;
    try {
      setIsLoading(true);
      setDate(newDate);
      const dueDateValue = moment(newDate).valueOf();
      if (data.cardType === "PLANNER") {
        const result = await updateCardAllInfoMutation({
          _id: data._id,
          title: data.title,
          description: data.description,
          priority: data.priority as "Low" | "Normal" | "High" | "Urgent",
          dueDate: dueDateValue,
          labelIds: data.labelIds,
          assignedUserId: data.assignedUserId
        });
        if (result?.error) {
          toast.error(result.error.message || "Failed to update due date");
          setDate(data.dueDate ? new Date(data.dueDate) : new Date()); // Reset if failed
          return;
        }
        toast.success(`Due date updated to ${format(newDate, "PPP")}`);
      } else {
        const result = await updateSubTodoAllInfoMutation({
          _id: data._id,
          title: data.title,
          description: data.description,
          priority: data.priority as "Low" | "Normal" | "High" | "Urgent",
          dueDate: dueDateValue,
          labelIds: data.labelIds
        });
        if (result?.error) {
          toast.error(result.error.message || "Failed to update due date");
          setDate(data.dueDate ? new Date(data.dueDate) : new Date()); // Reset if failed
          return;
        }
        toast.success(`Due date updated to ${format(newDate, "PPP")}`);
      }
    } catch (error) {
      console.error("[ExtraInfo.handleDateChange] - Error:", error);
      toast.error("Failed to update due date");
      setDate(data.dueDate ? new Date(data.dueDate) : new Date()); // Reset if failed
    } finally {
      setIsLoading(false);
    }
  };
  const handleAssignedUserChange = async (userId: string) => {
    try {
      setIsLoading(true);
      setAssignedTo(userId);
      const result = await updateCardAllInfoMutation({
        _id: data._id,
        title: data.title,
        description: data.description,
        priority: data.priority as "Low" | "Normal" | "High" | "Urgent",
        dueDate: data.dueDate,
        labelIds: data.labelIds,
        assignedUserId: userId as Id<"saasUsers">
      });
      if (result?.error) {
        toast.error(result.error.message || "Failed to update assigned user");
        setAssignedTo(data.assignedUserId || ""); // Reset if failed
        return;
      }
      toast.success("Assigned user updated");
    } catch (error) {
      console.error("[ExtraInfo.handleAssignedUserChange] - Error:", error);
      toast.error("Failed to update assigned user");
      setAssignedTo(data.assignedUserId || ""); // Reset if failed
    } finally {
      setIsLoading(false);
    }
  };
  const handleLabelsChange = async (selectedLabels: Doc<"labels">[]) => {
    const labelIds = selectedLabels.map(label => label._id);

    // Only update if the labels have actually changed
    const currentLabelIds = new Set(data.labelIds);
    const hasChanged = labelIds.length !== data.labelIds.length || labelIds.some(id => !currentLabelIds.has(id));
    if (!hasChanged) return;
    try {
      setIsLoading(true);
      if (data.cardType === "PLANNER") {
        const result = await updateCardAllInfoMutation({
          _id: data._id,
          title: data.title,
          description: data.description,
          priority: data.priority as "Low" | "Normal" | "High" | "Urgent",
          dueDate: data.dueDate,
          labelIds: labelIds,
          assignedUserId: data.assignedUserId
        });
        if (result?.error) {
          toast.error(result.error.message || "Failed to update labels");
          return;
        }
        toast.success("Labels updated");
      } else {
        const result = await updateSubTodoAllInfoMutation({
          _id: data._id,
          title: data.title,
          description: data.description,
          priority: data.priority as "Low" | "Normal" | "High" | "Urgent",
          dueDate: data.dueDate,
          labelIds: labelIds
        });
        if (result?.error) {
          toast.error(result.error.message || "Failed to update labels");
          return;
        }
        toast.success("Labels updated");
      }
    } catch (error) {
      console.error("[ExtraInfo.handleLabelsChange] - Error:", error);
      toast.error("Failed to update labels");
    } finally {
      setIsLoading(false);
    }
  };
  const divStyle = "flex flex-row gap-2 font-semibold p-2 rounded-md text-sm  border-transparent relative w-full focus-visible:bg-white dark:focus-visible:bg-white/20 focus-visible:border-input mb-0.5 ";
  const textStyle = "text-sm  text-neutral-700 dark:text-neutral-200 truncate";

  // Custom dropdown state
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Find selected user
  const selectedUser = allTeamMembers.find(member => member._id === assignedTo);
  return <div className="space-y-2" data-sentry-component="ExtraInfo" data-sentry-source-file="card-extra-info.tsx">
      <p className="text-sm font-semibold">Extra Info</p>

      {/* <p className="text-xs font-semibold">Priority</p>
       <div className={divStyle}>
        <Select onValueChange={handlePriorityChange} defaultValue={priority}>
          <SelectTrigger>
            <SelectValue placeholder="Select Priority" />
          </SelectTrigger>
          <SelectContent
            position="popper"
            side="bottom"
            align="start"
            sideOffset={4}
          >
            {["Low", "Normal", "High", "Urgent"].map((item) => (
              <SelectItem key={item} value={item}>
                {item}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
       </div> */}

      <p className="text-xs font-semibold">Due Date</p>
      <div className={divStyle}>
        <Popover data-sentry-element="Popover" data-sentry-source-file="card-extra-info.tsx">
          <PopoverTrigger asChild data-sentry-element="PopoverTrigger" data-sentry-source-file="card-extra-info.tsx">
            <Button variant={"outline"} className={cn("w-full justify-start rounded-lg text-left font-normal", !date && "text-muted-foreground")} disabled={isLoading} data-sentry-element="Button" data-sentry-source-file="card-extra-info.tsx">
              <CalendarIcon className="mr-2 h-4 w-4" data-sentry-element="CalendarIcon" data-sentry-source-file="card-extra-info.tsx" />
              {date ? format(date, "PPP") : <span>Pick a date</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start" sideOffset={4} data-sentry-element="PopoverContent" data-sentry-source-file="card-extra-info.tsx">
            <Calendar mode="single" selected={date} onSelect={newDate => handleDateChange(newDate)} initialFocus disabled={isLoading} data-sentry-element="Calendar" data-sentry-source-file="card-extra-info.tsx" />
          </PopoverContent>
        </Popover>
      </div>

      {data.cardType === "PLANNER" && <>
          <p className="text-xs font-semibold" id="assign-to-label">
            Assigned To
          </p>
          <div className={divStyle}>
            <Select value={assignedTo || ""} onValueChange={handleAssignedUserChange} disabled={isLoading}>
              <SelectTrigger className="h-10 w-full">
                <SelectValue placeholder="Select team member">
                  {selectedUser && <div className="flex items-center gap-2">
                      <NextUIAvatar src={selectedUser.image || ""} name={selectedUser.username || selectedUser.name || selectedUser.email} size="sm" className="flex-shrink-0 scale-80" />
                      <span className="text-sm">
                        {selectedUser.username || selectedUser.name || selectedUser.email}
                      </span>
                    </div>}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="z-[99999]" position="popper" sideOffset={4}>
                {allTeamMembers.map(member => <SelectItem key={member._id} value={member._id} className="flex py-2">
                    <div className="flex items-center gap-2">
                      <NextUIAvatar src={member.image || ""} name={member.username || member.name || member.email} size="sm" className="flex-shrink-0 scale-90" />
                      <div className="flex flex-col">
                        <span className="text-sm">
                          {member.username || member.name || member.email}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {member.email}
                        </span>
                      </div>
                    </div>
                  </SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </>}

      <p className="text-xs font-semibold">Labels</p>
      <div className="rounded-md border px-3 pt-2">
        <TagCreator parentId={orgId!} getSelectedTags={handleLabelsChange} defaultTags={availableLabels} defaultSelectedTags={tags} data-sentry-element="TagCreator" data-sentry-source-file="card-extra-info.tsx" />
      </div>
    </div>;
};
ExtraInfo.Skeleton = function ExtraInfoSkeleton() {
  return <div className="mt-2 space-y-2">
      <Skeleton className="h-4 w-20 bg-neutral-200 dark:bg-neutral-700 " />
      <Skeleton className="h-8 w-full bg-neutral-200 dark:bg-neutral-700 " />
      <Skeleton className="h-8 w-full bg-neutral-200 dark:bg-neutral-700 " />
    </div>;
};